import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type JourneyTimelineItem = {
  type: string;
  id: string;
  label: string;
  title: string;
  status: string;
  date: string;
};

class CustomerLifecycleService {
  async get360(customerId: string) {
    const res = await api.get(`/sales/customers/${customerId}/360`);
    return unwrap<{
      customer: Record<string, unknown>;
      journey_timeline: JourneyTimelineItem[];
      interactions?: {
        last_contact_at: string | null;
        open_follow_ups: number;
        overdue_follow_ups: number;
        activities_90d: number;
      };
      presale: Record<string, unknown>;
      fulfillment: Record<string, unknown>;
      financial: Record<string, unknown>;
      customer_success: Record<string, unknown>;
    }>(res);
  }

  async issuePortalToken(customerId: string, body?: { scope?: string; expires_days?: number }) {
    const res = await api.post(`/sales/customers/${customerId}/portal-token`, body ?? {});
    return unwrap<{ token: string; portal_path: string; expires_at: string }>(res);
  }

  async getSuccessDashboard() {
    const res = await api.get('/sales/customer-success/dashboard');
    return unwrap<{
      pending_surveys: number;
      completed_surveys: number;
      active_plans: number;
      avg_nps: number | null;
    }>(res);
  }

  async listSurveys(params?: { customer_id?: string; status?: string }) {
    const q = new URLSearchParams();
    if (params?.customer_id) q.set('customer_id', params.customer_id);
    if (params?.status) q.set('status', params.status);
    const res = await api.get(`/sales/customer-success/surveys?${q}`);
    return unwrap<unknown[]>(res) ?? [];
  }

  async getPortalWorkspace(token: string) {
    const res = await api.get(`/sales/public/customer-portal/${token}`);
    return unwrap<Record<string, unknown>>(res);
  }

  async submitPortalSurvey(token: string, surveyId: string, body: { nps_score?: number; csat_score?: number; feedback_text?: string }) {
    const res = await api.post(`/sales/public/customer-portal/${token}/surveys/${surveyId}`, body);
    return unwrap<unknown>(res);
  }

  async getSalesPipelineIntelligence() {
    const res = await api.get('/owner-intelligence/sales-pipeline-intelligence');
    return unwrap<Record<string, unknown>>(res);
  }
}

const customerLifecycleService = new CustomerLifecycleService();
export default customerLifecycleService;
